/*
  Maps this store's product options to London Dynamics 3D scene feature/variant
  IDs. Consumed by component-product-ar-launcher.js.

  Each rower line (Hybrid, Original, Lite, Metallic) points at its own scene
  — same London Dynamics customer ID, different sku — with its own feature
  list, variant IDs, and defaults:
  - Hybrid: model, wood-type, wood-frame-1/2, logo, monitor, expandable-arm,
    holder. No hirise-adaptor or rails.
  - Original: same as Hybrid, plus hirise-adaptor and rails. Monitor's
    default is s4-bluetooth rather than cr-bluetooth.
  - Lite: same shape as Hybrid, but the current product's Shopify variants
    only ever expose a single value for model/wood-type/monitor — the scene
    itself supports more than the catalog does (see variantIdByOptionValue.
    monitor's comment).
  - Metallic: "model" (lorise/hirise) and "choose-colour" (cat-black/
    arctic-silver) are real Shopify options here, unlike Hybrid/Original
    where model is fixed per scene. No wood-type. "Frame" and "Logo" mirror
    colour/branding internally but aren't customer-facing (see
    accessoryFeatureIds below). Monitor variants are s4-bluetooth/
    touchscreen-pro only.

  Nothing above is safe to assume carries over between scenes — check a
  scene's own getCurrentConfiguration() rather than trusting another scene's
  entry here, or a vendor-supplied spec, when the two disagree. Sending a
  feature ID a scene doesn't have is usually harmless on its own — the SDK
  silently ignores it (see wood-frame-1/2 below, ignored on every scene
  checked so far) — BUT confirmed live 2026-09-29 that's not true when an
  unrecognised key rides in the SAME setSelection call as a real change:
  bundling 'Frame'/'Logo' (Metallic-only, see accessoryFeatureIds below)
  alongside wood-type on the Original scene got wood-type accepted
  internally (getCurrentSelection echoed it back) but silently never
  visually re-rendered, no error either — isolating the same real change
  without the unrecognised keys worked every time. Whatever the SDK's
  batching does with an unrecognised key, "harmless no-op" only holds when
  that key is the ONLY thing in the call — component-product-ar-launcher.js's
  accessoryDefaults() scopes Frame/Logo to the Metallic scene specifically
  rather than sending them everywhere and trusting they're ignored.
*/
window.ProductConfiguratorMap = {
  // Shopify product option name -> LD feature ID, for features driven by
  // real Shopify variant options.
  shopifyOptionToFeature: {
    'Wood Type': 'wood-type',
    // The Shopify option is named "Performance Technology".
    'Performance Technology': 'monitor',
    // Only present on waterrower-original.
    'Rail Length': 'rails',
    // Only a real, live Shopify option on waterrower-metallic — fixed per
    // scene everywhere else.
    'Frame Height': 'model',
    // Metallic's real customer-facing colour choice, distinct from the
    // internal-only "Frame" feature (see accessoryFeatureIds below). The
    // real Shopify option is named "Metallic Finish" (confirmed live via
    // the storefront product JSON on the parent and both Black/Silver
    // children on 2026-10-07) — not "Metal Finish" or "Color", neither of
    // which exist on this product and were silently never matching, which
    // is why the scene always rendered its own default (Black) regardless
    // of which finish was actually selected.
    'Metallic Finish': 'choose-colour',
  },

  // Shopify option VALUE (lower-cased) -> LD variant ID, per feature.
  variantIdByOptionValue: {
    'wood-type': {
      oak: 'oak',
      cherry: 'cherry',
      shadow: 'shadow',
      walnut: 'walnut',
      vintage: 'vintage',
      club: 'club',
    },
    // Shared across scenes — a scene only ever recognises the subset of
    // these that's actually in its own feature list (see the per-scene
    // notes at the top of this file).
    monitor: {
      'cr bluetooth': 'cr-bluetooth',
      's4 bluetooth': 's4-bluetooth',
      powerpulley: 'power-pulley',
      'power pulley': 'power-pulley',
      'citirow connect touchscreen': 'city-row',
      'cityrow connect touchscreen': 'city-row',
      'city row': 'city-row',
      'pro screen': 'touchscreen-pro',
      'pro touchscreen': 'touchscreen-pro',
      // Lite's own monitor variants, now merchandised as a real (hidden)
      // Performance Technology option on the Lite product itself, same
      // pattern as Original's monitor - without an entry here,
      // applyHiddenVariantOption() can never find a matching hidden input
      // for either value, so syncHiddenVariantOption() always returns
      // false and both picks fall back to being billed as their own
      // separate cart line item instead of swapping the real variant.
      's4 performance monitor': 's4-performance',
      's4 performance monitor + commodule': 'performance-bluetooth',
    },
    rails: {
      'standard rails': 'regular',
      regular: 'regular',
      'xl rails': 'xl',
      xl: 'xl',
    },
    model: {
      lorise: 'lorise',
      hirise: 'hirise',
    },
    // Metallic only. Real variant IDs are lowercase-kebab, same convention
    // as every other feature. Keys here are the real Shopify option
    // values for "Metallic Finish" ("Black" / "Silver"), lower-cased —
    // confirmed live via the storefront product JSON on 2026-10-07, not
    // the "Metallic Black" / "Metallic Silver" previously assumed here.
    'choose-colour': {
      black: 'cat-black',
      silver: 'arctic-silver',
    },
  },

  // Feature IDs not driven by a Shopify variant option — always sent at
  // their scene default as a baseline. accessoryOverrides() in the launcher
  // then overwrites that baseline for whichever of these also has a real
  // accessory-drawer pick (expandable-arm, holder) on a given product.
  // wood-frame-1/2 are handled separately (see woodFrameMirrorsWoodType
  // below) rather than defaulted blind. Frame/Logo are Metallic's
  // equivalent of logo — internal, not customer-facing.
  accessoryFeatureIds: ['expandable-arm', 'logo', 'holder', 'hirise-adaptor', 'Frame', 'Logo'],

  // wood-frame-1/2 (frame accent parts) always mirror whichever Wood Type
  // is selected, rather than being independently merchandised. Variant IDs
  // match wood-type's 1:1 except "club", which splits into two distinct
  // real IDs — wood-frame-1 uses "club-black", wood-frame-2 uses
  // "club-red".
  //
  // The SDK ignores setSelection for these two feature IDs on every scene
  // checked so far. Kept as a harmless no-op rather than removed, in case a
  // scene's own config ever exposes them as writable — see the matching
  // comment in component-product-ar-launcher.js's mirrorWoodFrames.
  woodFrameMirrorsWoodType: {
    featureIds: ['wood-frame-1', 'wood-frame-2'],
    variantIdOverridesByWoodType: {
      club: { 'wood-frame-1': 'club-black', 'wood-frame-2': 'club-red' },
    },
  },

  // Scene-reported defaults for the accessoryFeatureIds above. "monitor" is
  // deliberately absent — every real variant of it is a paid/required
  // monitor with no "none" option, so it should only ever come from a real
  // Shopify option value, never defaulted blind.
  defaultVariantByFeature: {
    'expandable-arm': 'none',
    // Rail Length stays a real (now-hidden) Shopify variant option rather
    // than a true accessoryFeatureIds entry - this default is only consumed
    // by component-pdp-drawers.js's revertHiddenVariantOption, to put the
    // hidden option back to Standard when the Accessories drawer's "XL
    // Rails" checkbox gets unchecked (a checkbox has no "the other radio
    // got picked instead" moment to fall back on the way a single-select
    // drawer does).
    rails: 'regular',
    'wood-frame-1': 'oak',
    'wood-frame-2': 'oak',
    logo: 'none',
    holder: 'phone-holder',
    'hirise-adaptor': 'standard',
    Frame: 'cat-black',
    Logo: 'White Logo',
  },

  // Some accessory-drawer products are shared across multiple rower lines'
  // drawers (e.g. "WaterRower ComModule" appears in both Hybrid's and
  // Original's Performance Tech drawer), but the real variant ID it should
  // resolve to differs per scene (cr-bluetooth on Hybrid, s4-bluetooth on
  // Original). Tag a shared product's ld_variant_id metafield with the
  // semantic placeholder on the left below instead of a literal scene ID,
  // and the launcher resolves it against the active scene's sku here.
  variantIdAliasBySku: {
    'waterrower-hybrid': { 'bluetooth-monitor': 'cr-bluetooth' },
    'waterrower-original': { 'bluetooth-monitor': 's4-bluetooth' },
  },

  // How the PDP title changes with the selected Performance Technology
  // (the drawer item's ld_variant_id on the 'monitor' feature), per the
  // client's "Performance Tech Rower Names" sheet (BugHerd #84). `insert`
  // goes after the first two words (the "WaterRower Original" part, before
  // any wood/finish), `append` goes on the end. A monitor not listed here
  // (S4/CR Bluetooth, Lite's plain S4 Performance) keeps the product's own
  // title. Consumed by updateProductTitle in component-pdp-drawers.js.
  productTitleByMonitor: {
    'power-pulley': { insert: 'Performance' },
    'city-row': { insert: 'CityRow' },
    'touchscreen-pro': { insert: 'Pro' },
    'performance-bluetooth': { append: 'with ComModule' },
  },

  // Cross-feature dependency rules, keyed by scene sku — unlike everything
  // else in this file, these are genuinely per-scene rather than shared, so
  // don't assume a rule on one scene applies to another.
  //
  // Enforced client-side because we render our own Shopify-side
  // pdp_option_drawer UI rather than London Dynamics' native menu (hidden
  // via show-configurator="false" on the element) — whatever their scene
  // CMS does with these same rules server-side has no way to reach our
  // drawer's inputs.
  //
  // Two shapes:
  // - `triggers`: keyed "<featureId>:<variantId>". When the resolved
  //   selection has that feature sitting at that variant, every feature ID
  //   listed under `disable`/`hide` gets disabled/hidden by the launcher.
  //   Every rule so far lists the same feature IDs under both, but they're
  //   kept as separate lists rather than assumed always identical.
  // - `postProcess(selection)`: for dependencies that aren't a single
  //   trigger — e.g. Hybrid/Original's holder visibility depends on an OR
  //   across two different features (arm added, or a screen-less monitor
  //   chosen).
  //
  // component-product-ar-launcher.js resolves these to a fixed point
  // (revert, re-check, repeat) rather than a single pass, since reverting
  // one blocked feature can itself flip another rule's trigger — see
  // resolveRules there.
  rulesBySku: {
    'waterrower-hybrid': {
      triggers: {
        'monitor:city-row': { disable: ['expandable-arm'], hide: ['expandable-arm'] },
        'monitor:power-pulley': { disable: ['expandable-arm'], hide: ['expandable-arm'] },
        'monitor:touchscreen-pro': { disable: ['expandable-arm'], hide: ['expandable-arm'] },
      },
      // PowerPulley has no built-in screen, so the holder is relevant even
      // without the arm attached.
      postProcess(selection) {
        const holderVisible = selection['expandable-arm'] === 'expandable-arm' || selection['monitor'] === 'power-pulley';
        return holderVisible ? {} : { disable: ['holder'], hide: ['holder'] };
      },
    },
    'waterrower-original': {
      triggers: {
        'monitor:city-row': { disable: ['expandable-arm'], hide: ['expandable-arm'] },
        'monitor:power-pulley': { disable: ['expandable-arm'], hide: ['expandable-arm'] },
        'monitor:touchscreen-pro': { disable: ['expandable-arm'], hide: ['expandable-arm'] },
      },
      postProcess(selection) {
        const holderVisible = selection['expandable-arm'] === 'expandable-arm' || selection['monitor'] === 'power-pulley';
        return holderVisible ? {} : { disable: ['holder'], hide: ['holder'] };
      },
    },
    // No screen-less monitor option here (unlike Hybrid/Original's
    // PowerPulley), so holder visibility depends purely on the arm.
    'waterrower-lite': {
      triggers: {
        'expandable-arm:none': { disable: ['holder'], hide: ['holder'] },
      },
    },
    // touchscreen-pro plays the same arm-blocking role here that city-row/
    // power-pulley play on Hybrid/Original.
    'waterrower-metallic': {
      triggers: {
        'expandable-arm:none': { disable: ['holder'], hide: ['holder'] },
        'monitor:touchscreen-pro': { disable: ['expandable-arm'], hide: ['expandable-arm'] },
      },
    },
  },
};
